import { useState, useEffect, useCallback } from "react";
import { isApiConnected } from "@/lib/apiClient";
import { listFeedback, type FeedbackRow } from "@/services/feedbackService";

// Placeholder/test values seen from real form-testing sessions (e.g. name
// "test", email "test@gmail.com", feedback "test") and generic filler text —
// matched as a whole trimmed value, not a substring, so a genuine feedback
// that happens to mention "demo" isn't caught.
const DUMMY_VALUE = /^(test|testing|demo|dummy|sample|asdf+|xyz|abc|n\/a|na|none|nil|lorem ipsum.*|good|nice|ok(ay)?|great)$/i;
const DUMMY_EMAIL_DOMAIN = /@(test|example|dummy|sample)\.(com|in|org|net)$/i;

// Feedback should read as a personal account of a completed course, not a
// question/support request directed at the institute — those belong on
// Contact/Support, not the public feedback wall.
const QUERY_LIKE_START = /^(how|when|why|what|where|who|which|can i|can you|could you|would you|please (help|tell|let|advise|reply|respond)|is there|are there|do you|does|will you|i want to know|i need|i have a (query|question|doubt)|query[:\-]|question[:\-])\b/i;

function isGenuineFeedback(f: FeedbackRow): boolean {
  const name = f.name.trim();
  const email = f.email.trim();
  const feedback = f.feedback.trim();

  if (!name || !feedback) return false;
  if (DUMMY_VALUE.test(name) || DUMMY_VALUE.test(feedback)) return false;
  if (DUMMY_EMAIL_DOMAIN.test(email)) return false;
  // Genuine feedback reads as at least a short sentence, not a one-word
  // placeholder — filters out junk like "asdfgh" or "..." without penalizing
  // real short praise, which still tends to run longer than this.
  if (feedback.length < 15) return false;
  if (QUERY_LIKE_START.test(feedback) || feedback.endsWith("?")) return false;

  return true;
}

// Public homepage feed of 4- and 5-star, genuine feedback only (templateID
// 12 — see feedbackService.ts) — dummy/test submissions and support-style
// queries are filtered out so the homepage never shows placeholder or
// off-topic content. Distinct from useAdminFeedback in useAdminData.ts,
// which is the admin panel's unfiltered view of every submission (admins
// still need to see test/junk rows to manage them).
export function useFeaturedFeedback(): {
  feedback: FeedbackRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!isApiConnected()) {
      setFeedback([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    listFeedback()
      .then((rows) => {
        const genuine = rows
          .filter((r) => r.experienceRate >= 4 && isGenuineFeedback(r))
          .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
        setFeedback(genuine);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load feedback"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { feedback, loading, error, refresh: load };
}
